import React, { useEffect, useState } from "react";
import styled, { withTheme } from "styled-components";
import { PasswordInput } from "../../ui-library/AuthControls/PasswordInput";
import { regex } from "../../../modules/constants";
import { Button } from "../../ui-library/AuthControls/Button";
import Alert from "../../ui-library/Alert/Alert";
import PwChangeSuccessIcon from "../../../assets/svg/pw_change_success.svg";
import LoadingSpinner from "../../ui-library/LoadingSpinner/LoadingSpinner";
import { useTranslation } from "react-i18next";

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const PasswordDefine = (props) => {
  const {
    isTokenValid,
    submittedChange,
    passwordToken,
    change,
    setPassword,
    accountUsername,
    setModalTitle,
    showLoginModal,
    goHome,
    token,
    email,
    errorMessage,
    resetErrorMessage,
  } = props;

  const { t } = useTranslation();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validPassword, setValidPassword] = useState(false);
  const [validConfirmPassword, setValidConfirmPassword] = useState(false);
  const [passwordsTouched, setPasswordsTouched] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [shake, setShake] = useState(false);

  const onPasswordChange = (e) => {
    setNewPassword(e.target.value);
    setPassword(e.target.value);
    if (errorMessage) {
      resetErrorMessage();
    }
  };

  const onConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (errorMessage) {
      resetErrorMessage();
    }
  };

  const handleBlur = (field) => {
    setPasswordsTouched((prev) => {
      const updated = { ...prev, [field]: true };
      return updated;
    });
  };

  const onDefineSubmit = (event) => {
    event.preventDefault();
    if (
      newPassword !== confirmPassword ||
      !validPassword ||
      !validConfirmPassword
    ) {
      setShake(true);
      setTimeout(() => setShake(false), 300); // Reset shake effect after animation
      return;
    }
    const authToken = passwordToken || token;
    const user = email || accountUsername;
    change(authToken, user);
  };

  const goHomeHandler = (e) => {
    e.preventDefault();
    goHome();
  };

  useEffect(() => {
    if (newPassword && regex.test(newPassword)) {
      setValidPassword(true);
    } else {
      setValidPassword(false);
    }
  }, [newPassword]);

  useEffect(() => {
    if (confirmPassword && regex.test(confirmPassword)) {
      setValidConfirmPassword(true);
    } else {
      setValidConfirmPassword(false);
    }
  }, [confirmPassword]);

  useEffect(() => {
    if (submittedChange) {
      setModalTitle(t("appText.changePasswordConfirmation.title"));
    }
  }, [setModalTitle, submittedChange, t]);

  const showPasswordErrorMessage = (error) => {
    if (error) {
      if (typeof error === "object") {
        let newErrorMessage = "";
        for (const [, value] of Object.entries(error.errors)) {
          newErrorMessage += `${value} `;
        }

        return (
          <StyledError>
            <span>{newErrorMessage}</span>
          </StyledError>
        );
      } else if (typeof error === "string") {
        return (
          <StyledError>
            <span>{error}</span>
          </StyledError>
        );
      }
    }

    return null;
  };

  return !(isTokenValid?.length <= 0) || passwordToken ? (
    isTokenValid !== false ? (
      submittedChange ? (
        <div data-testid="success-modal">
          <Alert
            callback={showLoginModal}
            icon={PwChangeSuccessIcon}
            btnText={t("appText.login")}
            message={
              passwordToken
                ? `${t(
                    "appText.changePasswordConfirmation.message.success"
                  )} ${t("appText.changePasswordConfirmation.message.login")}`
                : t("appText.changePasswordConfirmation.message.success")
            }
          />
        </div>
      ) : (
        <form
          onSubmit={onDefineSubmit}
          className="auth-form"
          data-testid="password-define-view"
        >
          <PasswordInput
            placeholder={t("appText.newPassword")}
            onChange={onPasswordChange}
            onBlur={() => handleBlur("newPassword")}
            value={newPassword}
            margin="mb-3"
            field="1"
            autoFocus={true}
            inputError={shake}
          />
          <PasswordInput
            placeholder={t("appText.confirmPassword")}
            onChange={onConfirmPasswordChange}
            onBlur={() => handleBlur("confirmPassword")}
            value={confirmPassword}
            field="2"
            inputError={shake}
          />
          {passwordsTouched.newPassword && passwordsTouched.confirmPassword && (
            <StyledError data-testid="validation-error">
              {(!validPassword || !validConfirmPassword) && (
                <span className="invalid-input">
                  {t("errorText.auth.changePassword.validation")}
                </span>
              )}
              {newPassword !== confirmPassword &&
                validPassword &&
                validConfirmPassword && (
                  <span>{t("errorText.auth.changePassword.notMatch")}</span>
                )}
            </StyledError>
          )}
          {showPasswordErrorMessage(errorMessage)}
          <Button
            text={t("appText.submit")}
            disabled={!(newPassword && confirmPassword)}
          />
        </form>
      )
    ) : (
      <form onSubmit={goHomeHandler}>
        <p>{t("appText.auth.linkExpired")}</p>
        <Button text={t("appText.home")} />
      </form>
    )
  ) : (
    <LoadingSpinner />
  );
};

export default withTheme(PasswordDefine);
